import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { listBrokerCredentials } from '../utils/savedSecrets';
import { findBrokerConnection } from '../utils/brokerConnectionCatalog';
import { getBrokerAccountLabel, getBrokerConnectionTitle } from '../utils/brokerConnectionDisplay';
import './BrokerConnectionSelector.css';

const brokerNeedsReconnect = (item) => item?.status === 'reconnect_required';

export default function BrokerConnectionSelector({
  getApiToken,
  selectedId,
  onSelect,
  readOnly = false,
  title = 'Broker',
  subtitle = 'Choose where this bot should trade.',
  actionLabel = 'Add broker connection',
  emptyTitle = 'No broker connections yet. Add one to start this bot.',
}) {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const onSelectRef = useRef(onSelect);

  const returnTo = encodeURIComponent(`${location.pathname}${location.search || ''}`);
  const addPath = `/account-settings/broker-connections?action=add&returnTo=${returnTo}`;

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const loadItems = useCallback(async () => {
    if (!getApiToken) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken();
      const nextItems = await listBrokerCredentials(token);
      setItems(nextItems);
      const activeItems = nextItems.filter((item) => !brokerNeedsReconnect(item));
      if (!selectedId && activeItems.length > 0) {
        onSelectRef.current(activeItems[0].id, activeItems[0]);
      } else if (selectedId) {
        const selectedItem = activeItems.find((item) => item.id === selectedId);
        if (selectedItem) {
          onSelectRef.current(selectedItem.id, selectedItem);
        } else {
          onSelectRef.current('', null);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load broker connections');
    } finally {
      setLoading(false);
    }
  }, [getApiToken, selectedId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  return (
    <div className="broker-connection-selector">
      <div className="broker-connection-selector__header">
        <div>
          <div className="broker-connection-selector__title">{title}</div>
          <div className="broker-connection-selector__subtitle">
            {subtitle}
          </div>
        </div>
        {!readOnly && items.length > 0 && (
          <Button as={Link} to={addPath} variant="primary" size="sm" className="broker-connection-selector__primary-action">
            {actionLabel}
          </Button>
        )}
      </div>

      {loading && (
        <div className="broker-connection-selector__status">
          <Spinner size="sm" animation="border" /> Loading broker connections...
        </div>
      )}

      {error && <Alert variant="warning">{error}</Alert>}

      {!loading && !error && items.length === 0 && (
        <div className="broker-connection-selector__empty">
          <div>{emptyTitle}</div>
          {!readOnly && <Button as={Link} to={addPath} variant="primary" className="broker-connection-selector__primary-action">{actionLabel}</Button>}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="broker-connection-selector__list">
          {items.map((item) => {
            const brokerInfo = findBrokerConnection(item.broker);
            const isSelected = selectedId === item.id;
            const accountLabel = getBrokerAccountLabel(item);
            const itemTitle = getBrokerConnectionTitle(item) || item.displayName || brokerInfo?.name || item.broker;
            const modeLabel = item.paperOrLive === 'live' ? 'Live' : 'Paper';
            const needsReconnect = brokerNeedsReconnect(item);
            const reconnectPath = `/account-settings/broker-connections?action=add&broker=${encodeURIComponent(item.broker)}&authMethod=oauth&returnTo=${returnTo}`;
            const mainContent = (
              <>
                <span className="broker-connection-selector__item-main">
                  <span className="broker-connection-selector__item-name">{itemTitle}</span>
                  <span className="broker-connection-selector__item-meta">
                    {[brokerInfo?.name || item.broker, modeLabel, accountLabel].filter(Boolean).join(' · ')}
                  </span>
                  {needsReconnect && (
                    <span className="broker-connection-selector__item-warning">
                      Reconnect this broker before starting a bot.
                    </span>
                  )}
                </span>
                <span className="broker-connection-selector__radio" aria-hidden="true">
                  {needsReconnect ? 'Reconnect' : isSelected ? 'Selected' : 'Select'}
                </span>
              </>
            );
            if (needsReconnect) {
              return (
                <div
                  key={item.id}
                  className="broker-connection-selector__item needs-reconnect"
                >
                  {mainContent}
                  {!readOnly && (
                    <Link
                      className="broker-connection-selector__reconnect-link"
                      to={reconnectPath}
                    >
                      Reconnect {brokerInfo?.name || item.broker}
                    </Link>
                  )}
                </div>
              );
            }
            return (
              <button
                key={item.id}
                type="button"
                className={`broker-connection-selector__item ${isSelected ? 'selected' : ''}`}
                disabled={readOnly}
                onClick={() => onSelect(item.id, item)}
              >
                {mainContent}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
